import {IconButton, Label} from 'platform/components';
import {Box, Center, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useRef} from 'react';

import {isNotNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {getFileIconKey} from '../utils/getFileIconKey';

const ACCEPT = '.xlsx,.xls,.csv';
const ACCEPTED_EXTENSIONS = ACCEPT.split(',').map((s) => s.trim().toLowerCase());
const MAX_SIZE_BYTES = 50 * 1024 * 1024;

export interface ImportUploadBoxProps extends TestIdProps {
  selectedFile: File | Nullish;
  onFileSelect: (file: File | null) => void;
}

export function ImportUploadBox(props: ImportUploadBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (isNotNil(file) && isAcceptedFileType(file) && file.size <= MAX_SIZE_BYTES) {
      props.onFileSelect(file);
    }
    event.target.value = '';
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (isNotNil(file) && isAcceptedFileType(file) && file.size <= MAX_SIZE_BYTES) {
      props.onFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onFileSelect(null);
  };

  const file = props.selectedFile;

  return (
    <Box data-testid={suffixTestId('importUploadBox', props)}>
      <VStack>
        <Label>{i18n.t('page.import.labels.uploadFile')}</Label>
        <HiddenFileInput
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={handleChange}
          tabIndex={-1}
          aria-hidden
        />
        {isNotNil(file) ? (
          <StyledFileSelectedContainer>
            <StyledFileCard>
              <Box padding={2} backgroundColor="palettes.green.20.100" borderRadius="small">
                <Icon value={getFileIconKey(file.name)} color="palettes.green.80.100" size={5} />
              </Box>
              <VStack spacing={0} grow={1} minWidth={0}>
                <Text size="small" alternative noWrap>
                  {file.name}
                </Text>
                <Text size="xSmall" color="secondary">
                  {formatFileSize(file.size)}
                </Text>
              </VStack>
              <IconButton
                icon="action/delete"
                size="small"
                priority="tertiary"
                onClick={handleRemove}
                aria-label={i18n.t('page.import.actions.removeFile')}
                data-testid={suffixTestId('removeFile', props)}
              />
            </StyledFileCard>
          </StyledFileSelectedContainer>
        ) : (
          <StyledDropZone
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            tabIndex={0}
            role="button"
            aria-label={i18n.t('page.import.labels.dropFileHere')}
            data-testid={suffixTestId('dropZone', props)}
          >
            <Center width="100%" height="100%">
              <VStack spacing={2} align="center">
                <Icon value="file/upload" color="palettes.blue.70.100" size={6} />
                <HStack spacing={1} align="center" wrap="nowrap">
                  <Text inline size="small" color="secondary">
                    {`${i18n.t('page.import.labels.dropFileHere')} `}
                  </Text>
                  <Text inline size="small" color="link" alternative>
                    {i18n.t('page.import.labels.browseFiles')}
                  </Text>
                </HStack>
                <Text size="xSmall" color="secondary">
                  {i18n.t('page.import.labels.supportedFormatsMaxSize')}
                </Text>
              </VStack>
            </Center>
          </StyledDropZone>
        )}
      </VStack>
    </Box>
  );
}

function isAcceptedFileType(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
`;

const StyledDropZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({theme}) => theme.getSize(27)};
  padding: ${({theme}) => theme.getSize(4)};
  background: ${({theme}) => theme.colors.palettes.blue[10][100]};
  border: 1px dashed ${({theme}) => theme.colors.palettes.blue[60][100]};
  border-radius: ${({theme}) => theme.radii.small};
  cursor: pointer;
  outline: none;

  &:hover {
    background: ${({theme}) => theme.colors.palettes.blue[20][100]};
    border-color: ${({theme}) => theme.colors.palettes.blue[70][100]};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({theme}) => theme.colors.palettes.blue[60][100]};
  }
`;

const StyledFileSelectedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({theme}) => theme.getSize(27)};
  padding: ${({theme}) => theme.getSize(4)};
  background: ${({theme}) => theme.colors.palettes.green[10][100]};
  border: 1px solid ${({theme}) => theme.colors.palettes.green[60][100]};
  border-radius: ${({theme}) => theme.radii.small};
`;

const StyledFileCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  width: 100%;
  max-width: ${({theme}) => theme.getSize(80)};
  padding: ${({theme}) => theme.getSize(2)} ${({theme}) => theme.getSize(3)};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  border: 1px solid ${({theme}) => theme.colors.palettes.neutral[80][100]};
  border-radius: ${({theme}) => theme.radii.small};
`;
